import { ChangeDetectionStrategy, Component, inject, input, Signal, ViewChild } from "@angular/core";
import { AppConfig } from "@api/models/app-config";
import { AppConfigService } from "@api/services/app-config.service";
import { AccessToken } from "@auth/models/token";
import { TokenService } from "@auth/services/token/token.service";
import { menuPtOptions } from "@layout/components/help-menu/menu-pt";
import { IHelpMenuItem } from "@layout/models/help-menu-item";
import { ToastService } from "@shared/services/toast/toast.service";
import { ICenter } from "@shared/models/center";
import { SessionStorageService } from "@shared/services/session-storage.service";
import { SessionStorageKeys } from "@shared/services/static/session-storage-keys";
import { ButtonPassThrough, Button } from "primeng/button";
import { Menu } from "primeng/menu";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faCircleQuestion, faEnvelope, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { faArrowUpRightFromSquare, faLaptop } from "@fortawesome/pro-solid-svg-icons";

@Component({
    selector: "app-help-menu",
    templateUrl: "./help-menu.component.html",
    styleUrls: ["./help-menu.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Button, FaIconComponent, Menu],
})
export class HelpMenuComponent {
    public menuPt = menuPtOptions;
    public faCircleQuestion = faCircleQuestion;
    public faArrowUpRightFromSquare = faArrowUpRightFromSquare;

    @ViewChild("menu") private menu!: Menu;

    public pt = input<ButtonPassThrough | null>(null);

    helpMenu: IHelpMenuItem[] = [];

    private readonly appConfig: Signal<AppConfig>;
    private readonly tokenService = inject(TokenService);
    private readonly sessionStorage = inject(SessionStorageService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly toastService = inject(ToastService);

    constructor() {
        this.appConfig = this.appConfigService.appConfig;
        this.helpMenu = [
            // HACK: For the first iteration, we should skip the first entry in the drop down below “Help Documentation”. This would be for ACW specific documentation, which we do not have yet.
            // {
            //     label: 'Help Documentation',
            //     faIcon: ['fas', 'book'],
            //     external: true,
            //     url: this.helpDocsUrl,
            //     target: '_blank'
            // },
            {
                label: "Visit Support Portal",
                faIcon: faLaptop,
                external: true,
                url: this.appConfig().supportPortalUrl,
                target: "_blank",
            },
            {
                label: "Email Support",
                faIcon: faEnvelope,
                sub: this.appConfig().supportEmail,
                command: () => this.reportIssue(),
            },
            {
                label: "Call Support",
                faIcon: faPhone,
                right: ["Monday-Friday", "7AM-6PM EST"],
                sub: this.appConfig().supportPhone.replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3"),
                url: "tel:" + this.appConfig().supportPhone,
            },
        ];
    }

    toggle(event: Event): void {
        this.menu?.toggle(event);
    }

    private reportIssue(): void {
        const d = new Date();
        const token: AccessToken | null = this.tokenService.getToken();
        const storedHealthCenter = this.sessionStorage.getItem(SessionStorageKeys.ScopedCenter);

        if (!token || !storedHealthCenter) {
            this.toastService.showErrorToast("Oops", "Something went wrong. Please try again later");

            return;
        }

        const healthCenter: ICenter = JSON.parse(storedHealthCenter);

        let body = "";
        body += "****Please call 781-365-2213 to speak with a support representative directly****%0D%0A%0D%0A";
        body += "Please include a detailed description of the issue:%0D%0A%0D%0A";
        body += "Thank you,%0D%0A%0D%0A";
        body += "Azara Support%0D%0A%0D%0A%0D%0A%0D%0A%0D%0A%0D%0A";
        body += "Please do not modify the below information, this is diagnostics for our team. Thank you!%0D%0A";

        body += `User: ${token.userEmail}%0D%0A`;
        body += `Tenant: ${token.tenant}%0D%0A`;
        body += `Center: ${healthCenter.name}%0D%0A`;
        body += `Time: ${d.toISOString()}%0D%0A`;
        body += `Page: ${document.title}%0D%0A`;
        body += `URL: ${window.location.href.replace("#", "")}%0D%0A`;
        body += `User Agent: ${navigator.userAgent}%0D%0A`;

        const mailto = `mailto:${this.appConfig().supportEmail}?subject=Support%20Request&body=${body}`;
        window.location.href = mailto;
    }
}
